import { useState, useEffect, lazy, Suspense } from "react";
import { BrowserRouter, MemoryRouter, Routes, Route, Navigate } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/sonner";
import { SubscriptionProvider } from "@/contexts/SubscriptionContext";
import { storage } from "@/lib/storage";
import { initPushNotifications, ensureDeviceRegistered, checkPushPermissions, clearAppBadge } from "@/lib/pushNotifications";
import Layout from "@/components/Layout";
import "@/App.css";

// Lazy load pages with preloading for critical paths
const LandingPage = lazy(() => import("@/pages/LandingPage"));
const AuthPage = lazy(() => import("@/pages/AuthPage"));
const VerifyEmail = lazy(() => import("@/pages/VerifyEmail"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Contacts = lazy(() => import("@/pages/Contacts"));
const ContactDetail = lazy(() => import("@/pages/ContactDetail"));
const Templates = lazy(() => import("@/pages/Templates"));
const Profile = lazy(() => import("@/pages/Profile"));
const Settings = lazy(() => import("@/pages/Settings"));
const BirthdayCards = lazy(() => import("@/pages/BirthdayCards"));
const CardView = lazy(() => import("@/pages/CardView"));
const PricingPage = lazy(() => import("@/pages/PricingPage"));
const LegalMentions = lazy(() => import("@/pages/LegalMentions"));
const PrivacyPolicy = lazy(() => import("@/pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("@/pages/TermsOfService"));
const AdminPage = lazy(() => import("@/pages/AdminPage"));
const SMSCredits = lazy(() => import("@/pages/SMSCredits"));
const PaymentSuccess = lazy(() => import("@/pages/PaymentSuccess"));
const PaymentCancel = lazy(() => import("@/pages/PaymentCancel"));
// Cadeaux et Cartes Postales temporairement désactivés pour le lancement
// const GiftsPage = lazy(() => import("@/pages/GiftsPage"));
// const PostcardsPage = lazy(() => import("@/pages/PostcardsPage"));
const OnboardingFlow = lazy(() => import("@/pages/OnboardingFlow"));
const MessagePacksShop = lazy(() => import("@/pages/MessagePacksShop"));

// Preload critical pages after initial render
const preloadCriticalPages = () => {
  // Preload most used pages in background
  setTimeout(() => {
    import("@/pages/Dashboard");
    import("@/pages/Contacts");
  }, 1000);
  setTimeout(() => {
    import("@/pages/ContactDetail");
    import("@/pages/BirthdayCards");
  }, 2000);
};

// Loading fallback component - ultra léger
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="animate-spin rounded-full h-6 w-6 border-2 border-primary border-t-transparent"></div>
  </div>
);

// Detect native app safely
let isNativeApp = false;
try {
  isNativeApp = Capacitor.isNativePlatform();
} catch (e) {
  isNativeApp = false;
}

// Initialize push notifications on native app startup - re-register device if permissions already granted
if (isNativeApp) {
  // Initialize push notification listeners IMMEDIATELY on app start
  // This must happen before iOS sends the token
  initPushNotifications().then(() => {
    console.log("[APP] Push notification listeners initialized");
  }).catch(err => {
    console.log("[APP] Push notification init error:", err);
  });
  
  // Clear badge when app opens or comes to foreground
  const clearBadgeOnForeground = async () => {
    try {
      const token = await storage.getItem("skybday_token");
      if (token) {
        console.log("[APP] Clearing app badge...");
        await clearAppBadge();
      }
    } catch (e) {
      console.log("[APP] Error clearing badge:", e);
    }
  };
  
  // Clear badge on app start
  clearBadgeOnForeground();
  
  // Listen for app state changes using standard web API (works with Capacitor)
  document.addEventListener('visibilitychange', async () => {
    if (document.visibilityState === 'visible') {
      console.log("[APP] App became visible, clearing badge...");
      await clearBadgeOnForeground();
    }
  });
  
  // Also listen for resume event on mobile
  document.addEventListener('resume', async () => {
    console.log("[APP] App resumed, clearing badge...");
    await clearBadgeOnForeground();
  });
  
  // Delayed registration to allow app to fully load and auth token to be available
  setTimeout(async () => {
    try {
      console.log("[APP] Checking authentication for push notification registration...");
      
      // First check if user is authenticated
      const token = await storage.getItem("skybday_token");
      if (!token) {
        console.log("[APP] User not authenticated, skipping push notification registration");
        return;
      }
      
      // CRITICAL: Set the auth token in the API client BEFORE any API calls
      const { setAuthToken } = await import("@/lib/api");
      setAuthToken(token);
      console.log("[APP] Auth token set in API client");
      
      const hasPermission = await checkPushPermissions();
      console.log("[APP] Push permission status:", hasPermission);
      
      if (hasPermission) {
        console.log("[APP] Push permissions granted, ensuring device is registered...");
        await ensureDeviceRegistered();
        console.log("[APP] Device registration attempt completed");
      }
    } catch (e) {
      console.log("[APP] Push notification registration error:", e);
    }
  }, 3000); // Increased delay to ensure app is fully loaded
  
  // Deep link handling for skybday:// URL scheme
  const setupDeepLinkListener = async () => {
    try {
      const { App } = await import("@capacitor/app");
      
      // Handle deep link when app is opened via URL
      App.addListener('appUrlOpen', ({ url }) => {
        console.log('[APP] Deep link received:', url);
        
        // Parse the URL - format: skybday://auth or skybday://dashboard etc.
        if (url.startsWith('skybday://')) {
          const path = url.replace('skybday://', '/');
          console.log('[APP] Navigating to:', path);
          
          // Use window.location for MemoryRouter navigation
          // This will be handled by the app's routing
          window.deepLinkPath = path;
          
          // Dispatch a custom event that the app can listen to
          window.dispatchEvent(new CustomEvent('deeplink', { detail: { path } }));
        }
      });
      
      // Check if app was opened with a URL (cold start)
      const { url } = await App.getLaunchUrl() || {};
      if (url && url.startsWith('skybday://')) {
        const path = url.replace('skybday://', '/');
        console.log('[APP] App launched with deep link:', path);
        window.deepLinkPath = path;
      }
      
      console.log('[APP] Deep link listener initialized');
    } catch (e) {
      console.log('[APP] Error setting up deep link listener:', e);
    }
  };
  
  setupDeepLinkListener();
}

// Use MemoryRouter for native apps, BrowserRouter for web
const Router = isNativeApp ? MemoryRouter : BrowserRouter;

// Protected route with async token check for native
const ProtectedRoute = ({ children }) => {
  const [isLoading, setIsLoading] = useState(isNativeApp);
  const [hasToken, setHasToken] = useState(!isNativeApp && !!storage.getItemSync("skybday_token"));

  useEffect(() => {
    const initAuth = async () => {
      if (isNativeApp) {
        const token = await storage.getItem("skybday_token");
        if (token) {
          // CRITICAL: Set the auth token in API client BEFORE rendering protected content
          const { setAuthToken } = await import("@/lib/api");
          setAuthToken(token);
          console.log("[ProtectedRoute] Auth token set in API client");
        }
        setHasToken(!!token);
        setIsLoading(false);
      }
    };
    initAuth();
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!hasToken) {
    return <Navigate to="/auth" replace />;
  }
  return children;
};

function App() {
  // Get initial path from deep link or default to /auth for native apps
  const getInitialPath = () => {
    if (!isNativeApp) return undefined;
    if (window.deepLinkPath) {
      console.log('[App] Using deep link path:', window.deepLinkPath);
      return [window.deepLinkPath];
    }
    return ["/auth"];
  };
  
  // Preload critical pages after mount
  useEffect(() => {
    preloadCriticalPages();
  }, []);
  
  return (
    <HelmetProvider>
      <Router initialEntries={getInitialPath()}>
        <SubscriptionProvider>
          <div className="App min-h-screen bg-background">
            <div className="noise-overlay" />
            <Suspense fallback={<PageLoader />}>
              <Routes>
                {/* Public routes */}
                <Route path="/" element={<LandingPage />} />
                <Route path="/auth" element={<AuthPage />} />
                <Route path="/onboarding" element={<OnboardingFlow />} />
                <Route path="/verify-email" element={<VerifyEmail />} />
                <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/legal" element={<LegalMentions />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<TermsOfService />} />
              <Route path="/card/:shareCode" element={<CardView />} />
              
              {/* Protected routes */}
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <Dashboard />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/contacts"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <Contacts />
                    </Layout>
                  </ProtectedRoute>
                }
              />
            <Route
              path="/contacts/:id"
              element={
                <ProtectedRoute>
                  <Layout>
                    <ContactDetail />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/templates"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Templates />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/message-packs"
              element={
                <ProtectedRoute>
                  <Layout>
                    <MessagePacksShop />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Profile />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Settings />
                  </Layout>
                </ProtectedRoute>
              }
            />
            {/* Cadeaux et Cartes Postales temporairement désactivés pour le lancement
            <Route
              path="/gifts"
              element={
                <ProtectedRoute>
                  <Layout>
                    <GiftsPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/postcards"
              element={
                <ProtectedRoute>
                  <Layout>
                    <PostcardsPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            */}
            <Route
              path="/pricing"
              element={
                <ProtectedRoute>
                  <Layout>
                    <PricingPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/cards/:contactId"
              element={
                <ProtectedRoute>
                  <Layout>
                    <BirthdayCards />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/cards"
              element={
                <ProtectedRoute>
                  <Layout>
                    <BirthdayCards />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/sms-credits"
              element={
                <ProtectedRoute>
                  <Layout>
                    <SMSCredits />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin"
              element={
                <ProtectedRoute>
                  <Layout>
                    <AdminPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            {/* Stripe Web Payment Routes */}
            <Route
              path="/payment/success"
              element={
                <ProtectedRoute>
                  <Layout>
                    <PaymentSuccess />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/payment/cancel"
              element={
                <ProtectedRoute>
                  <Layout>
                    <PaymentCancel />
                  </Layout>
                </ProtectedRoute>
              }
            />
            </Routes>
          </Suspense>
          <Toaster position="top-right" theme="dark" />
          <div id="confetti-container" />
        </div>
      </SubscriptionProvider>
    </Router>
    </HelmetProvider>
  );
}

export default App;
